import React, { useContext, useEffect, useRef, useState } from "react";
import { isMobile } from "react-device-detect";
import "./UserPreferenceWidget.css";
import {
  addOrUpdateStyle,
  getStyleByAgentId,
  deleteStyleApi,
} from "../../../../../service/MasterService";
import studyReportContext from "../../../../../context/studyreport/StudyReportContext";
import toastContext from "../../../../../context/toast/ToastContext";
import { TOAST_TYPE } from "../../../../../constants/Constants";
import { Editor } from "slate";
import { DefaultButton, PrimaryButton } from "@fluentui/react";
import AgenticReportingContext from "../../../../../context/agentic-reporting/AgenticReportingContext";
import { useTranslation } from "react-i18next";

const UserPreferenceWidget = ({ isOpen, onClose, anchorRect }) => {
  const { llmReportTitle, activeTabKey } = useContext(studyReportContext);
  const { selectedReportMention, agenticReportAgents } = useContext(
    AgenticReportingContext,
  );
  const { showToastv2 } = useContext(toastContext);
  const [styleContent, setStyleContent] = useState("");
  const [styleId, setStyleId] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [titleForQuery, setTitleForQuery] = useState("");
  const textareaRef = useRef(null);
  const [activeAgentId, setActiveAgentId] = useState(null);
  const [activeAgentName, setActiveAgentName] = useState("");
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen && !isLoading && textareaRef.current) {
      const textarea = textareaRef.current;
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd = textarea.value.length;
    }
  }, [isOpen, isLoading]);

  useEffect(() => {
    if (isOpen) {
      const fetchStyleData = async () => {
        setIsLoading(true);
        let resolvedAgentId = selectedReportMention?.value;
        const activeMention = selectedReportMention?.label;

        // Fallback to resolve agentId if it's missing (e.g. if tabs were initialized before agents finished loading)
        if (!resolvedAgentId && activeMention && agenticReportAgents?.length > 0) {
          const matchingAgent = agenticReportAgents.find(
            (a) => a.label?.toLowerCase() === activeMention.toLowerCase()
          );
          if (matchingAgent) {
            resolvedAgentId = matchingAgent.value;
          }
        }

        setTitleForQuery(activeMention || "");
        setActiveAgentId(resolvedAgentId);
        setActiveAgentName(activeMention || "");
        if (!resolvedAgentId) {
          setIsLoading(false);
          return;
        }

        try {
          const response = await getStyleByAgentId(resolvedAgentId);
          if (response?.data?.result === "SUCCESS") {
            const responseData = response.data.data;
            if (responseData && responseData.id) {
              setStyleContent(responseData.styles || "");
              setStyleId(responseData.id);
              setIsEditMode(true);
            } else {
              setStyleContent("");
              setStyleId(null);
              setIsEditMode(false);
            }
            setIsReady(true);
          } else {
            showToastv2(
              TOAST_TYPE.ERROR,
              response?.data?.message || t("userPreference.fetchStyleFailure"),
            );
          }
        } catch (err) {
          showToastv2(
            TOAST_TYPE.ERROR,
            t("userPreference.unexpectedNetworkError"),
          );
          console.error("Fetch error:", err);
        } finally {
          setIsLoading(false);
        }
      };

      fetchStyleData();
    }
  }, [
    isOpen,
    llmReportTitle,
    selectedReportMention,
    activeTabKey,
    agenticReportAgents,
  ]);

  const handleSaveOrUpdate = async () => {
    if (!activeAgentId) {
      showToastv2(TOAST_TYPE.ERROR, t("userPreference.activeAgentRequired"));
      return;
    }

    const trimmedContent = styleContent.trim();
    if (trimmedContent.length > 500) {
      showToastv2(TOAST_TYPE.ERROR, t("userPreference.preferenceMaxLength", { max: 500 }));
      return;
    }

    if (isEditMode && !trimmedContent) {
      setIsSaving(true);
      try {
        const response = await deleteStyleApi({ id: styleId });
        if (response?.data?.result === "SUCCESS") {
          showToastv2(TOAST_TYPE.SUCCESS, t("userPreference.deletedSuccess"));
          setStyleContent("");
          setStyleId(null);
          setIsEditMode(false);
          onClose();
        } else {
          showToastv2(
            TOAST_TYPE.ERROR,
            response?.data?.message || t("userPreference.deleteFailure"),
          );
        }
      } catch (err) {
        showToastv2(
          TOAST_TYPE.ERROR,
          t("userPreference.unexpectedDeleteError"),
        );
        console.error("Delete error:", err);
      } finally {
        setIsSaving(false);
      }
      return;
    }

    setIsSaving(true);

    const payload = {
      ...(isEditMode && styleId && { id: Number(styleId) }),
      agentId: activeAgentId,
      ...(trimmedContent && { styles: trimmedContent }),
    };

    try {
      const response = await addOrUpdateStyle(payload);
      if (response?.data?.result === "SUCCESS") {
        showToastv2(
          TOAST_TYPE.SUCCESS,
          isEditMode
            ? t("userPreference.updatedSuccess")
            : t("userPreference.addedSuccess"),
        );
        onClose();
      } else {
        showToastv2(
          TOAST_TYPE.ERROR,
          response?.data?.message || t("userPreference.saveFailure"),
        );
      }
    } catch (err) {
      showToastv2(
        TOAST_TYPE.ERROR,
        t("userPreference.unexpectedSaveError"),
      );
      console.error("Save error:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const closeButtonStyles = {
    root: {
      backgroundColor: "#ffffff",
      color: "#333333",
      borderColor: "#8a8886",
      borderWidth: "1px",
      borderStyle: "solid",
    },
    rootHover: {
      backgroundColor: "#f3f2f1",
      color: "#201f1e",
    },
    rootPressed: {
      backgroundColor: "#edebe9",
      color: "#201f1e",
    },
  };

  const fixedStyle = anchorRect
    ? isMobile
      ? {
        bottom: window.innerHeight - anchorRect.top + 8,
        right: window.innerWidth - anchorRect.right,
        "--widget-slide-from": "translateY(10px)",
      }
      : {
        top: anchorRect.bottom + 8,
        right: window.innerWidth - anchorRect.right,
        "--widget-slide-from": "translateY(-10px)",
      }
    : {};

  return (
    <div className={`floating-widget ${isOpen ? "open" : ""} ${isMobile ? "opens-up" : ""}`} style={fixedStyle}>
      <div className="widget-header">
        <h4>Your preferences: {activeAgentName}</h4>
      </div>

      {!isReady ? (
        <div className="widget-loading">Loading...</div>
      ) : (
        <textarea
          ref={textareaRef}
          className="widget-textarea"
          value={styleContent}
          onChange={(e) => setStyleContent(e.target.value)}
          placeholder="Enter your custom preference here..."
        />
      )}

      {isReady && (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-end",
            marginTop: "10px",
            gap: "10px",
          }}
        >
          <PrimaryButton
            text={isSaving ? (isEditMode && !styleContent.trim() ? "Deleting..." : "Saving...") : isEditMode && !styleContent.trim() ? "Delete" : isEditMode ? "Update" : "Add"}
            onClick={handleSaveOrUpdate}
            disabled={isLoading || isSaving || (!isEditMode && !styleContent.trim())}
          />
          <DefaultButton
            text="Close"
            onClick={onClose}
            disabled={isSaving}
            styles={closeButtonStyles}
          />
        </div>
      )}
    </div>
  );
};

export default UserPreferenceWidget;
